import { backtestSummaryFromNav } from "../Pages/StrategyPerformance/backtestMetrics";

/**
 * Calculate monthly and annual performance data for the performance table
 * @param {Object} strategyNavSeries - Object with date keys (YYYY-MM-DD) and NAV values
 * @returns {Array} Array of performance data objects with year, monthly returns, and annual metrics
 */
export function calculatePerformanceTableData(strategyNavSeries) {
  if (!strategyNavSeries || typeof strategyNavSeries !== 'object') {
    return [];
  }

  // Convert NAV series object to Map
  const navSeries = new Map(
    Object.entries(strategyNavSeries).map(([date, nav]) => [date, nav])
  );

  if (navSeries.size === 0) {
    return [];
  }

  // Get all dates and extract unique years
  const dates = Array.from(navSeries.keys()).sort((a, b) => a.localeCompare(b));
  const years = [...new Set(dates.map(date => date.split('-')[0]))].sort();

  const performanceData = [];

  // Options for backtestSummaryFromNav
  const options = {
    tradingDays: 252,
    riskFreeRateAnnual: 0.02,
    ddof: 1,
    sortinoThreshold: 0,
    ignoreNonTradingDays: true,
  };

  // Process each year
  years.forEach(year => {
    const yearData = {
      year: year,
      jan: null,
      feb: null,
      mar: null,
      apr: null,
      may: null,
      jun: null,
      jul: null,
      aug: null,
      sep: null,
      oct: null,
      nov: null,
      dec: null,
      twr: null,
      sharpe: null,
      sortino: null,
      largestDrawdown: null,
    };

    // Calculate monthly returns
    const months = [
      { key: 'jan', num: 1, name: 'January' },
      { key: 'feb', num: 2, name: 'February' },
      { key: 'mar', num: 3, name: 'March' },
      { key: 'apr', num: 4, name: 'April' },
      { key: 'may', num: 5, name: 'May' },
      { key: 'jun', num: 6, name: 'June' },
      { key: 'jul', num: 7, name: 'July' },
      { key: 'aug', num: 8, name: 'August' },
      { key: 'sep', num: 9, name: 'September' },
      { key: 'oct', num: 10, name: 'October' },
      { key: 'nov', num: 11, name: 'November' },
      { key: 'dec', num: 12, name: 'December' },
    ];

    months.forEach(month => {
      // Get first and last day of the month (calendar boundaries)
      const monthStart = `${year}-${String(month.num).padStart(2, '0')}-01`;

      // Get last day of month (handle leap years for February)
      let lastDay = 31;
      if (month.num === 2) {
        const isLeapYear = (parseInt(year) % 4 === 0 && parseInt(year) % 100 !== 0) || (parseInt(year) % 400 === 0);
        lastDay = isLeapYear ? 29 : 28;
      } else if ([4, 6, 9, 11].includes(month.num)) {
        lastDay = 30;
      }

      const monthEnd = `${year}-${String(month.num).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      // Check if we have data for this month - filter strictly by year and month
      const monthDates = dates.filter(d => {
        const [y, m] = d.split('-');
        const dateYear = y;
        const dateMonth = parseInt(m);
        return dateYear === year && dateMonth === month.num;
      }).sort((a, b) => a.localeCompare(b)); // Ensure sorted

      if (monthDates.length > 0) {
        try {
          // Use calendar month boundaries (selected real dates) - periodReturnFromNav will find nearest available dates
          const monthSummary = backtestSummaryFromNav(navSeries, monthStart, monthEnd, {
            ...options,
            mode: "calendar", // first NAV inside period
          });

          if (monthSummary && monthSummary.totalTimeWeightedReturnPct !== undefined) {
            const returnValue = monthSummary.totalTimeWeightedReturnPct;
            yearData[month.key] = returnValue;
          }
        } catch (error) {
          console.error(`Error calculating ${month.name} ${year}:`, error);
        }
      }
    });

    // Calculate annual metrics
    // Check if we have data for this year
    const yearDates = dates.filter(d => d.startsWith(year));

    if (yearDates.length > 0) {
      try {
        // Use calendar year boundaries (selected real dates) - periodReturnFromNav will find nearest available dates
        const yearStart = `${year}-01-01`;
        const yearEnd = `${year}-12-31`;

        const yearSummary = backtestSummaryFromNav(navSeries, yearStart, yearEnd, {
          ...options,
          mode: "calendar",
        });

        if (yearSummary) {
          yearData.twr = yearSummary.totalTimeWeightedReturnPct !== undefined
            ? yearSummary.totalTimeWeightedReturnPct
            : null;
          yearData.sharpe = yearSummary.sharpe !== undefined && !isNaN(yearSummary.sharpe)
            ? yearSummary.sharpe
            : null;
          yearData.sortino = yearSummary.sortino !== undefined && !isNaN(yearSummary.sortino)
            ? yearSummary.sortino
            : null;
          yearData.largestDrawdown = yearSummary.worstDrawdown?.drawdownPct !== undefined
            ? yearSummary.worstDrawdown.drawdownPct
            : null;
        }
      } catch (error) {
        console.error(`Error calculating annual metrics for ${year}:`, error);
      }
    }

    performanceData.push(yearData);
  });

  return performanceData;
}

